import { FeaturesReady, useFeatureIsOn } from '@growthbook/growthbook-react';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import classNames from 'classnames';
import { AppFeatures, FEATURES_READY_TIMEOUT_MS } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import InviteFriendsBanner from 'components/InviteFriendsBanner/InviteFriendsBanner';
import config, { Country, Language } from 'config';
import { FAQ, INDEX, MY, SELL_ADD_PRODUCT, SHOP, YAGA_EXTERNALS } from 'config/fe-routes';
import IconZendesk from 'public/icons/icn-zendesk.svg';
import LogoFacebook from 'public/icons/icon-fb.svg';
import LogoInstagram from 'public/icons/icon-insta.svg';
import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import IosCameraOutline from 'react-ionicons/lib/IosCameraOutline';
import { connect } from 'react-redux';
import { openLoginModal } from 'store/actions/login.actions';
import { openSideMenu } from 'store/actions/sidemenu.actions';
import { UserType } from 'types/user.types';
import { GTM, GTMButtons, GTMLocations } from 'utils/analytics';
import { logOut } from 'utils/authentication';
import { redirectTo } from 'utils/route-helpers';

import { useLanguage } from '../../hooks/useLanguage';
import { useIsScreenSizeMax } from '../../utils/screen-size';
import AppDownloadLinks from '../AppDownloadLinks/AppDownloadLinks';
import A from '../Link/Link';
import { useDropHomePageCache } from '../PageHome/PageHome';
import ProfileImage from '../ProfileImage/ProfileImage';
import SideMenuPrivacyLinks from './SideMenuPrivacyLinks';

type Anchor = 'top' | 'left' | 'bottom' | 'right';
type Props = {
  sidemenuOpen: boolean;
  user: UserType;
  messagesCount: number;
  notificationsCount: number;
};
type SidemenuLink = {
  name: ReactNode;
  hidden?: boolean;
  href?: string;
  as?: string;
  onClick?: () => void;
  auth?: boolean;
  icons?: ReactElement[];
  countBadge?: number;
  subname?: string;
  className?: string;
};

// iOS has a "swipe to go back" feature that interferes with the discovery feature, so discovery has to be disabled.
// @ts-ignore Ignore type error.
const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);

const mapStateToProps = ({ sideMenuData, userData, messagesCountData }) => ({
  sidemenuOpen: sideMenuData.sidemenuOpen,
  user: userData && userData.user,
  messagesCount: messagesCountData.messages,
  notificationsCount: messagesCountData.notifications,
});

const isReferralsEnabled = config.get('referrals.enabled');
const buyerProtectionFeeEnabled = config.get('buyerProtectionFeeEnabled');

const SideMenu = (props: Props) => {
  const { sidemenuOpen, user, messagesCount } = props;

  const { t } = useTranslation();
  const { country, languageName, language } = useLanguage();
  const isSmallScreen = useIsScreenSizeMax('md');
  const canChangeLanguage = useFeatureIsOn<AppFeatures>('can_change_language');

  const isLoggedIn = user?.isLoggedIn;
  const toShop = isLoggedIn ? { ...SHOP(user?.shop?.activeSlug) } : { ...INDEX() };

  const sideMenuAnchor: Anchor = 'left';
  const dropHomePageCache = useDropHomePageCache();

  const socialFacebook = config.get(`web.socialMediaLinks[${country}].facebook`);
  const socialInstagram = config.get(`web.socialMediaLinks[${country}].instagram`);

  const zendeskEmailQueryParamKey = config.get('customerSupport.zendesk.fields.email');

  const hideBlogPage = (country === Country.EE && language === Language.en) || country === Country.LV;

  const sideMenuLinks: SidemenuLink[] = [
    {
      name: t('common.main-page'),
      ...INDEX(),
      onClick: () => dropHomePageCache(),
    },
    {
      name: t('pages.my-shop.title'),
      ...toShop,
      auth: true,
    },
    {
      name: t('pages.my-shop-orders.title'),
      ...MY.ORDERS(),
      auth: true,
    },
    {
      name: t('pages.purchases.title'),
      ...MY.PURCHASES(),
      auth: true,
    },
    {
      name: t('pages.account.title'),
      ...MY.WALLET(),
      auth: true,
    },
    {
      name: t('pages.messages.title'),
      ...MY.MESSAGES(),
      auth: true,
      countBadge: messagesCount,
    },
    {
      name: t('pages.promocodes.title'),
      ...MY.PROMOCODES(),
      auth: true,
      subname: isReferralsEnabled ? t('pages.promocodes.subtitle') : null,
    },
    {
      name: t('pages.settings.title'),
      ...MY.SETTINGS(),
      auth: true,
    },
  ];

  const bottomLinks: SidemenuLink[] = [
    {
      name: (
        <Grid container justifyContent="space-between" alignItems="center" gap={1} wrap="nowrap" className="language-selector">
          <Grid item className="language-selector-icon-wrapper">
            <img src="/icons/icn-earth.svg" alt="" className="language-selector-icon" />
          </Grid>
          <Grid item flexGrow={1}>
            {languageName}
          </Grid>
          <Grid item className="icon-forward-wrapper">
            <ArrowForwardIosIcon key="sidemenu-arrow-forward" fontSize="small" className="icon-forward" />
          </Grid>
        </Grid>
      ),
      ...MY.SETTINGS_LANGUAGE(),
      className: 'my-1',
      hidden: !isSmallScreen || !canChangeLanguage,
    },
    {
      name: t('common.yaga-faq'),
      ...FAQ(),
      icons: [
        <A href={socialInstagram} key="sidemenu-icon-insta" aria-label="insta-btn" target="_blank" rel="noopener noreferrer">
          <LogoInstagram />
        </A>,
        <A href={socialFacebook} key="sidemenu-icon-fb" aria-label="facebook-btn" target="_blank" rel="noopener noreferrer">
          <LogoFacebook />
        </A>,
      ],
    },
    {
      name: t('common.support'),
      ...YAGA_EXTERNALS.SUPPORT(),
      hidden: config.get('customerSupport.zendesk.enabled'),
    },
    {
      name: t('common.blog'),
      ...YAGA_EXTERNALS.BLOG(),
      hidden: hideBlogPage,
    },
    {
      name: t('common.log-out'),
      onClick: () => logOut(),
      hidden: !isLoggedIn,
    },
    {
      name: t('common.log-in'),
      onClick: () => openLoginModal(true),
      hidden: isLoggedIn,
    },
  ];

  // Sidemenu handler
  const toggleSideMenu = (_, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent | any) => {
    if (event && event.type === 'keydown' && ['Tab', 'Shift'].includes(event.key)) {
      return;
    }

    openSideMenu(open);
  };

  const profile = (
    <div className="profile-block">
      {/* profile */}
      <ListItem disableGutters={true} alignItems="center" className="profile-details">
        <A className="no-style profile-details-link" {...MY.SETTINGS()} onClick={() => openSideMenu(false)}>
          <div className="user-name">
            <h4 className="user-fname">{user?.shop?.name}</h4>
            <div className="user-slug">@{user?.shop?.activeSlug}</div>
          </div>

          <ProfileImage src={user?.picture} size={45} className="user-image" />
        </A>
      </ListItem>

      {/* add product button */}
      <ListItem disableGutters={true} className="sell">
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            openSideMenu(false);

            GTM.buttonClicked(GTMButtons.ADD_PRODUCT, GTMLocations.SIDEBAR);

            redirectTo(SELL_ADD_PRODUCT(undefined, 'side_menu'));
          }}
          startIcon={
            <div className="add-product-icons">
              <IosCameraOutline className="add-product-btn-icon" />
              <AddIcon className="add-icon" />
            </div>
          }
          className="add-product-btn"
        >
          <span>{t('common.add-product')}</span>
        </Button>
      </ListItem>
    </div>
  );

  // Sidemenu content
  const list = (anchor: Anchor) => (
    <div className="sidemenu main-side-padding" role="presentation">
      {/* start selling button */}

      <List className="first-block">
        {/* profile */}
        {isLoggedIn ? (
          profile
        ) : (
          <ListItem disableGutters={true} className="sell">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                openSideMenu(false);
                redirectTo(SELL_ADD_PRODUCT(undefined, 'side_menu'));
              }}
              startIcon={<AddIcon className="start-selling-btn-icon" />}
              className="start-selling-btn"
            >
              <span>{t(buyerProtectionFeeEnabled ? 'common.start-selling-for-free' : 'common.start-selling')}</span>
            </Button>
          </ListItem>
        )}
      </List>

      {/* Invite friends (DEV-1841 / DEV-2320). The banner picks the control or reward arm itself. */}
      {isLoggedIn && (
        <FeaturesReady timeout={FEATURES_READY_TIMEOUT_MS}>
          <InviteFriendsBanner isOpen={sidemenuOpen} onNavigate={() => openSideMenu(false)} />
        </FeaturesReady>
      )}

      {/* links */}
      <List className="links">
        {sideMenuLinks
          .filter((link) => (link.href || link.onClick) && !link.hidden)
          .map((sideMenuLink, idx) => (
            <ListItem
              button
              // Include idx so we don't collide when two entries point at the
              // same href (e.g. both Home and "My Shop"-when-logged-out resolve
              // to INDEX() and would share `/` as a key).
              key={sideMenuLink?.href ? `${sideMenuLink.href}-${idx}` : sideMenuLink.name.toString()}
              divider={true}
              onClick={(event) => {
                // If auth needed and not logged in, trigger login modal
                if (sideMenuLink.auth && !isLoggedIn) {
                  openLoginModal(true, {
                    href: sideMenuLink.href,
                    as: sideMenuLink.as,
                  });
                } else if (sideMenuLink.onClick) {
                  // Run onClick if loggedIn
                  sideMenuLink.onClick();
                }
                toggleSideMenu(anchor, false)(event);
              }}
              onKeyDown={toggleSideMenu(anchor, false)}
              disableGutters={true}
              className={classNames('sidemenu-link', sideMenuLink.className)}
            >
              <A
                className="no-style"
                href={sideMenuLink.auth && !isLoggedIn ? undefined : sideMenuLink.href}
                as={sideMenuLink.auth && !isLoggedIn ? undefined : sideMenuLink.as}
              >
                <ListItemText
                  primary={
                    <>
                      {sideMenuLink.name}

                      {!!sideMenuLink?.countBadge && <span className="sidemenu-link-badge">{sideMenuLink?.countBadge}</span>}
                    </>
                  }
                  secondary={<>{!!sideMenuLink?.subname && <span className="sidemenu-link-subname">{sideMenuLink?.subname}</span>}</>}
                />
              </A>
            </ListItem>
          ))}
      </List>

      {/* App ads */}
      <AppDownloadLinks mt={2} />

      {/* bottom links */}
      <List className="bottom-links">
        {/* Zendesk button */}
        {config.get('customerSupport.zendesk.enabled') && (
          <ListItem
            onClick={(event) => {
              toggleSideMenu(anchor, false)(event);
            }}
            onKeyDown={toggleSideMenu(anchor, false)}
            disableGutters={true}
            className="sidemenu-link zendesk-button"
          >
            <A
              className="no-style zendesk-link"
              target="_blank"
              {...YAGA_EXTERNALS.SUPPORT({
                yaga_prefill: 'true',
                [zendeskEmailQueryParamKey]: user?.email,
              })}
            >
              <IconZendesk className="icon-help-zendesk" />
              <span>{t('common.help-center')}</span>
            </A>
          </ListItem>
        )}

        {/* Blog button */}
        {bottomLinks
          .filter((bottomLink) => (bottomLink.href || bottomLink.onClick) && !bottomLink.hidden)
          .map((bottomLink) => (
            <div
              className={classNames('sidemenu-link-wrapper', bottomLink.className)} //
              key={bottomLink.href || bottomLink.name?.toString()}
            >
              <ListItem
                button
                onClick={(event) => {
                  if (bottomLink.onClick) {
                    bottomLink.onClick();
                  }

                  toggleSideMenu(anchor, false)(event);
                }}
                onKeyDown={toggleSideMenu(anchor, false)}
                disableGutters={true}
                className="sidemenu-link"
              >
                <A className="no-style" href={bottomLink.href} as={bottomLink?.as}>
                  <ListItemText primary={bottomLink.name} />
                </A>
              </ListItem>

              {/* sidemenu icons (social media) */}
              {bottomLink.icons && <div className="sidemenu-icons">{bottomLink.icons}</div>}
            </div>
          ))}
      </List>

      {/* show copyright if configured */}
      {config.copyright && (
        <div className="copyright">
          <span>
            {config.copyright} {new Date().getFullYear()}
          </span>
        </div>
      )}

      {/* Privacy component */}
      <div className="privacy-component">
        <SideMenuPrivacyLinks />

        {/* Company info */}
        <div className="company-info">
          <span>{t('sidemenu.company-info')}</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SwipeableDrawer
        anchor={sideMenuAnchor}
        open={sidemenuOpen}
        onClose={toggleSideMenu(sideMenuAnchor, false)}
        onOpen={toggleSideMenu(sideMenuAnchor, true)}
        disableDiscovery={iOS}
        disableSwipeToOpen
        hysteresis={0.3}
      >
        {list(sideMenuAnchor)}
      </SwipeableDrawer>
    </>
  );
};

export default connect(mapStateToProps)(SideMenu);
